'use client';

/**
 * Popular-categories rail + rotating hero banner, side by side.
 *
 * Port of ThemeMaker's {@code lms-portal CategoryBannerNew} (category
 * list + `CategoryBannerDetail` swiper), rebuilt on the site-builder
 * block conventions:
 * <ul>
 *   <li>Category list comes from {@code useLearningSource} (the same
 *       adapter {@code CategoryFilterPills} uses) instead of a direct
 *       Redux/service call — works on any host that wires the
 *       adapter.</li>
 *   <li>The banner side reuses {@code HeroCarousel}'s slide/track/
 *       arrows/dots/touch/keyboard logic rather than Swiper, so no new
 *       runtime dependency.</li>
 *   <li>Height auto-sizes to each slide's real image aspect ratio,
 *       exactly like {@code HeroCarousel}'s {@code height: 'auto'} mode
 *       — the panel adopts the image's own shape (via a natural-ratio
 *       probe) instead of a fixed/operator-set ratio, so nothing is
 *       ever cropped by {@code object-fit}. A fluid {@code clamp()}
 *       floor covers the instant before the first image loads.</li>
 *   <li>One CSS breakpoint (992px) switches the grid from stacked
 *       (mobile) to sidebar + banner (desktop); below it the category
 *       list becomes a horizontal scroll rail instead of disappearing
 *       into a dropdown.</li>
 * </ul>
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type TouchEvent,
} from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import {
  useLearningSource,
  isVisible,
  useCurrentUser,
  useBlockPart,
  type CurrentUser,
  type LearningCategory,
} from '@akademi/site-builder-runtime';
import { Button, Card } from '@akademi/ui';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { cardReset } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import type { CampaignAudience } from '../_shared/campaignAudience';
import { HeroEmptyFallback } from '../_shared/HeroEmptyFallback';
import { BlockRoot } from '../_shared/BlockRoot';

export interface HeroCategoryBannerTextField {
  text?: string;
  /** Defaults to white — assumes a dark overlay/image behind it. */
  color?: string;
}

export interface HeroCategoryBannerSlide extends CampaignAudience {
  title?: HeroCategoryBannerTextField;
  subtitle?: HeroCategoryBannerTextField;
  image?: {
    url?: string;
    /** Alt text for the background image. Falls back to {@code title.text}. */
    alt?: string;
  };
  cta?: {
    label?: string;
    href?: string;
  };
  /** Per-slide tint over the image — lets different slides use different
   *  overlay strengths (e.g. a busier photo needs more tint for legible
   *  text than a calmer one). */
  overlay?: {
    overlayColor?: string;
    /** 0..1 (clamped). Default 0.35. */
    overlayOpacity?: number;
  };
}

export interface HeroCategoryBannerProps {
  /** Limit how many categories show. Default 15. */
  categoryLimit?: number;
  /** Href template for a category row. {@code {id}} and {@code {slug}}
   *  are replaced. Default {@code /catalog?categoryId={id}}. */
  categoryHrefTemplate?: string;
  /** Rotating banner slides on the right (desktop) / bottom (mobile).
   *  Title/subtitle color and overlay tint are per-slide — see
   *  {@link HeroCategoryBannerSlide}. */
  slides?: HeroCategoryBannerSlide[];
  autoplay?: boolean;
  /** Min 2000 enforced at render. */
  autoplayDelay?: number;
  /** Default true. Ignored when {@code slides.length < 2}. */
  showArrows?: boolean;
  /** Default true. Ignored when {@code slides.length < 2}. */
  showDots?: boolean;
  radius?: 'none' | 'sm' | 'md' | 'lg';
  /** 'full' (default) lets the block span the section edge-to-edge.
   *  'theme' constrains it to the page's max-width container — same
   *  toggle/naming as HeroCarousel's `maxWidth`. */
  maxWidth?: 'full' | 'theme';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const RADIUS_MAP: Record<NonNullable<HeroCategoryBannerProps['radius']>, string> = {
  none: '0px',
  sm: 'var(--radius-sm, 6px)',
  md: 'var(--radius-md, 12px)',
  lg: 'var(--radius-lg, 20px)',
};

const SWIPE_THRESHOLD = 50;
const HORIZONTAL_LOCK_RATIO = 1.2;
// Candidate widths for the banner's srcSet — spans mobile-stacked
// full-width down low, up to the 1800px ceiling used for wide desktop
// viewports (see the `sizes` attribute on the <img> below).
const IMAGE_SRCSET_WIDTHS = [480, 800, 1200, 1800];

export function HeroCategoryBanner(props: HeroCategoryBannerProps) {
  const {
    categoryLimit = 15,
    categoryHrefTemplate = '/catalog?categoryId={id}',
    slides: rawSlides,
    autoplay = false,
    autoplayDelay = 5000,
    showArrows = true,
    showDots = true,
    radius = 'lg',
    maxWidth = 'full',
    paddingOuter,
    paddingInner,
  } = props;

  const uid = useId().replace(/:/g, '-');
  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);

  // CSS alone can't make the rail track the banner's height: grid has to
  // size the row from content before align-items: stretch applies, so an
  // overflow-auto sibling just renders at its own full content height
  // (no clipping) — measuring the banner directly and applying that as
  // the rail's max-height is the only way that's actually reliable.
  const bannerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = bannerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height;
      if (h) setBannerHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <BlockRoot
      as="section"
      style={{
        ...outerPad,
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: gridCss(uid) }} />
      <Card className={`hcb-${uid}`} style={{ ...gridStyle, ...innerPad }}>
        <div className={`hcb-cats-${uid}`} style={catsColStyle}>
          <CategoryRail
            limit={categoryLimit}
            hrefTemplate={categoryHrefTemplate}
            maxHeightPx={bannerHeight}
          />
        </div>
        <div style={bannerColStyle}>
          <BannerCarousel
            slides={rawSlides}
            autoplay={autoplay}
            autoplayDelay={autoplayDelay}
            showArrows={showArrows}
            showDots={showDots}
            radius={RADIUS_MAP[radius]}
            panelRef={bannerRef}
          />
        </div>
      </Card>
    </BlockRoot>
  );
}

// =============================================================================
// Category rail
// =============================================================================

function categoryHref(template: string, category: LearningCategory): string {
  const id = category.id;
  const slug = category.slug || id;
  return template
    .replace('{id}', encodeURIComponent(id))
    .replace('{slug}', encodeURIComponent(slug));
}

function CategoryRail({
  limit,
  hrefTemplate,
  maxHeightPx,
}: {
  limit: number;
  hrefTemplate: string;
  /** Desktop-only cap, measured live off the banner panel — see the
   *  ResizeObserver in HeroCategoryBanner. Null before the first
   *  measurement (initial paint / SSR); falls back to a fixed value. */
  maxHeightPx: number | null;
}) {
  const learning = useLearningSource();
  const uid = useId().replace(/:/g, '-');
  const { t } = useTranslation('common');

  // Mobile starts collapsed (accordion) — same pattern CategoryTree uses
  // for its collapsible panel. Defaults to open so the list is visible
  // without JS / before hydration; only collapses once we know we're on
  // a narrow viewport. Desktop ignores `open` entirely — see gridCss,
  // which forces the list visible there regardless of this state.
  const [open, setOpen] = useState(true);
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 992) setOpen(false);
  }, []);

  const state = useAsyncResource<LearningCategory[]>(async () => {
    if (!learning) return null;
    const cats = await learning.fetchCategories();
    return cats.length > 0 ? cats.slice(0, limit) : null;
  }, [learning, limit]);

  if (!learning) {
    return <AdapterPlaceholder blockType="HeroCategoryBanner" adapterName="LearningSource" />;
  }
  if (state.kind !== 'loaded') return null;

  return (
    <div style={railWrapStyle}>
      <style dangerouslySetInnerHTML={{ __html: railCss(uid) }} />
      <button
        type="button"
        className={`hcb-rail-toggle-${uid}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={railToggleStyle}
      >
        <RailChevron open={open} />
        <span>{t('title.catalog')}</span>
      </button>
      <ul
        className={`hcb-rail-list-${uid} ${open ? '' : 'hcb-rail-closed'}`}
        // Set as a CSS custom property, not a literal `maxHeight` — a
        // literal inline value would apply at every breakpoint and stomp
        // the mobile rail's own fixed height (see railCss). The variable
        // is only read by the desktop media-query rule; mobile ignores
        // it entirely. Fallback (450) only matters for the instant
        // before the ResizeObserver's first measurement lands.
        style={
          {
            ...railListStyle,
            ['--hcb-banner-h' as string]: `${maxHeightPx ?? 450}px`,
          } as CSSProperties
        }
      >
        {state.data.map((category) => (
          <li key={category.id} style={{ listStyle: 'none' }}>
            <Link
              href={categoryHref(hrefTemplate, category)}
              style={railRowStyle}
              className="hcb-row"
            >
              <span
                aria-hidden
                style={{
                  ...railIconStyle,
                  background: category.imageUrl
                    ? `url("${resizeImageUrl(category.imageUrl, '48x48')}") center/cover`
                    : 'var(--color-surface-muted, #f4f4f5)',
                }}
              />
              <span style={railLabelStyle} title={category.name}>
                {category.name}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RailChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{
        flexShrink: 0,
        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 180ms ease',
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// =============================================================================
// Banner carousel — HeroCarousel's slide/track/chrome logic, scoped to a
// sidebar-height panel instead of a full-bleed section.
// =============================================================================

function BannerCarousel({
  slides: rawSlides,
  autoplay,
  autoplayDelay,
  showArrows,
  showDots,
  radius,
  panelRef,
}: {
  slides?: HeroCategoryBannerSlide[];
  autoplay: boolean;
  autoplayDelay: number;
  showArrows: boolean;
  showDots: boolean;
  radius: string;
  /** Attached to the rendered panel root so the parent can measure its
   *  real height and size the category rail to match. */
  panelRef: React.RefObject<HTMLDivElement | null>;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const currentUser = useCurrentUser<CurrentUser>();
  const slides = useMemo(() => {
    const list = Array.isArray(rawSlides) ? rawSlides : [];
    return list
      .filter((s) => s && (s.title?.text || s.subtitle?.text || s.image?.url || s.cta?.label))
      .filter((s) => isVisible(s, currentUser));
  }, [rawSlides, currentUser]);

  const count = slides.length;
  const isCarousel = count > 1;

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [pageHidden, setPageHidden] = useState(false);

  // Natural width/height of each slide's image — same probe as
  // HeroCarousel's `height: 'auto'` mode. Anchored to slide 0 specifically
  // (not the *active* slide) — sizing off whichever slide is currently
  // showing made the whole panel visibly resize every autoplay tick
  // whenever slides had different image ratios (independent of
  // maxWidth: same bug at 'full' or 'theme'). Falls back to whichever
  // slide has actually loaded if slide 0 hasn't yet.
  const [naturalRatios, setNaturalRatios] = useState<Record<number, number>>({});
  const activeRatio = naturalRatios[0] ?? naturalRatios[index];
  const recordRatio = (i: number, img: HTMLImageElement) => {
    const { naturalWidth, naturalHeight } = img;
    if (!naturalWidth || !naturalHeight) return;
    const ratio = naturalWidth / naturalHeight;
    setNaturalRatios((prev) => (prev[i] === ratio ? prev : { ...prev, [i]: ratio }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sync = () => setPageHidden(document.hidden);
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  useEffect(() => {
    if (!isCarousel || !autoplay || reducedMotion || paused || pageHidden) return;
    const delay = Math.max(2000, autoplayDelay);
    const t = setTimeout(() => setIndex((i) => (i + 1) % count), delay);
    return () => clearTimeout(t);
  }, [index, isCarousel, autoplay, reducedMotion, paused, pageHidden, autoplayDelay, count]);

  const go = (next: number) => {
    if (!isCarousel) return;
    setIndex(((next % count) + count) % count);
  };
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const onTouchStart = (e: TouchEvent) => {
    if (!isCarousel) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e: TouchEvent) => {
    if (!isCarousel) return;
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dy) * HORIZONTAL_LOCK_RATIO > Math.abs(dx)) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
    if (dx < 0) next();
    else prev();
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (!isCarousel) return;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        prev();
        break;
      case 'ArrowRight':
        e.preventDefault();
        next();
        break;
    }
  };

  if (count === 0) {
    return (
      <div ref={panelRef} style={{ ...bannerPanelStyle, borderRadius: radius }}>
        <HeroEmptyFallback style={{ position: 'absolute', inset: 0 }} />
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      role={isCarousel ? 'region' : undefined}
      aria-roledescription={isCarousel ? 'carousel' : undefined}
      aria-label={isCarousel ? t('hero.carousel.categoryBanner') : undefined}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        ...bannerPanelStyle,
        borderRadius: radius,
        // Once the active slide's image reports its real dimensions,
        // size purely from that ratio — no maxHeight cap, matching
        // HeroCarousel's `auto` mode exactly. Capping height while width
        // keeps growing would make the box no longer match the image's
        // shape and reintroduce cropping/letterboxing — the thing this
        // ratio-based sizing exists to avoid. Falls back to the 16/9 +
        // maxHeight floor (bannerPanelStyle) only until the first image
        // loads.
        ...(activeRatio ? { height: 'auto', aspectRatio: activeRatio, maxHeight: 'none' } : {}),
      }}
      tabIndex={-1}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          width: `${count * 100}%`,
          transform: `translateX(-${index * (100 / count)}%)`,
          transition: reducedMotion ? 'none' : 'transform 480ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        }}
      >
        {slides.map((slide, i) => {
          const active = i === index;
          // Per-slide, not block-level: different slides can want
          // different overlay strength / text color (e.g. a busier photo
          // needs more tint than a calmer one). Falls back to the same
          // defaults the block used to apply uniformly.
          const overlayColor = slide.overlay?.overlayColor ?? '#000000';
          const clampedOpacity = Math.max(0, Math.min(1, slide.overlay?.overlayOpacity ?? 0.35));
          const titleColor = slide.title?.color || 'var(--color-text-primary, #ffffff)';
          const subtitleColor = slide.subtitle?.color || 'var(--color-text-primary, #ffffff)';
          return (
            <article
              key={i}
              role={isCarousel ? 'group' : undefined}
              aria-roledescription={isCarousel ? 'slide' : undefined}
              aria-label={
                isCarousel ? t('hero.carousel.slidePosition', { index: i + 1, count }) : undefined
              }
              aria-hidden={isCarousel && !active}
              inert={isCarousel && !active}
              style={{ flex: `0 0 ${100 / count}%`, position: 'relative' }}
            >
              {slide.image?.url && (
                <img
                  // Sidebar-height panel never needs a full-resolution
                  // banner image — request a right-sized asset instead
                  // of the original. The rendered width ranges from a
                  // full-width mobile stack (<992px) to `calc(100% -
                  // 260px)` next to the category rail (see gridCss) —
                  // srcSet lets the browser pick instead of always
                  // paying for the 1800px ceiling (measured: 261KB at
                  // 1800w vs 37KB at 480w for the same source image).
                  src={resizeImageUrl(slide.image.url, '1800x0')}
                  srcSet={IMAGE_SRCSET_WIDTHS.map((w) => {
                    const imageUrl = slide.image!.url;
                    return `${resizeImageUrl(imageUrl, `${w}x0`)} ${w}w`;
                  }).join(', ')}
                  sizes="(min-width: 992px) calc(100vw - 260px), 100vw"
                  alt={slide.image.alt || slide.title?.text || ''}
                  loading={i === 0 ? 'eager' : 'lazy'}
                  fetchPriority={i === 0 ? 'high' : undefined}
                  decoding="async"
                  onLoad={(e) => recordRatio(i, e.currentTarget)}
                  ref={(el) => {
                    if (el && el.complete) recordRatio(i, el);
                  }}
                  // `contain`, not `cover` — the panel adopts the image's
                  // own aspect ratio (see activeRatio above), so the full
                  // image is always visible at every size, never cropped.
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  {...(slide.image.alt === '' ? { role: 'presentation' } : {})}
                />
              )}
              {slide.image?.url ? (
                // Over the image: a tint at the configured opacity.
                clampedOpacity > 0 && (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: overlayColor,
                      opacity: clampedOpacity,
                    }}
                  />
                )
              ) : (
                // No image: overlayColor becomes the slide's solid
                // background instead of falling through to the panel's
                // default muted gray — so it isn't wasted on
                // image-less/text-only slides.
                <div
                  aria-hidden
                  style={{ position: 'absolute', inset: 0, background: overlayColor }}
                />
              )}
              {(slide.title?.text || slide.subtitle?.text || slide.cta?.label) && (
                <div
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    padding: 'clamp(var(--space-5, 20px), 4vw, var(--space-9, 36px))',
                    gap: 'var(--space-2, 8px)',
                    maxWidth: '80%',
                  }}
                >
                  {slide.title?.text && (
                    <h3
                      {...part('title', i)}
                      style={{
                        margin: 0,
                        fontSize: 'clamp(18px, 2.6vw, 28px)',
                        lineHeight: 'var(--line-height-tight, 1.2)',
                        fontWeight: 'var(--font-weight-bold, 700)',
                        fontFamily: 'var(--font-family-display, inherit)',
                        color: titleColor,
                      }}
                    >
                      {slide.title.text}
                    </h3>
                  )}
                  {slide.subtitle?.text && (
                    <p
                      {...part('subtitle', i)}
                      style={{
                        margin: 0,
                        fontSize: 'clamp(12px, 1.1vw, 14px)',
                        lineHeight: 'var(--line-height-normal, 1.4)',
                        color: subtitleColor,
                      }}
                    >
                      {slide.subtitle.text}
                    </p>
                  )}
                  {slide.cta?.label && (
                    <div style={{ marginTop: 'var(--space-2, 8px)' }}>
                      <Button href={slide.cta.href || '#'} size="lg" {...part('cta', i)}>
                        {slide.cta.label}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {isCarousel && showArrows && (
        <>
          <CarouselArrow direction="prev" onClick={prev} label={t('hero.carousel.previous')} />
          <CarouselArrow direction="next" onClick={next} label={t('hero.carousel.next')} />
        </>
      )}

      {isCarousel && showDots && (
        <div
          role="tablist"
          aria-label={t('hero.carousel.selector')}
          {...part('tablist')}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 'clamp(8px, 1.5vw, 14px)',
            display: 'flex',
            justifyContent: 'center',
            gap: 'var(--space-2, 8px)',
            zIndex: 2,
          }}
        >
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t('hero.carousel.goToSlide', { index: i + 1 })}
              onClick={() => go(i)}
              {...part('tab', i)}
              style={{
                // Hit area meets the 24x24 CSS px touch-target minimum even
                // though the visual pill (below) stays small.
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: i === index ? 24 : 24,
                height: 24,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: i === index ? 22 : 8,
                  height: 8,
                  borderRadius: 'var(--radius-pill, 999px)',
                  background: i === index ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)',
                  transition: 'width 220ms ease, background 220ms ease',
                }}
              />
            </button>
          ))}
        </div>
      )}

      {isCarousel && (
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          {t('hero.carousel.slidePosition', { index: index + 1, count })}
        </div>
      )}
    </div>
  );
}

function CarouselArrow({
  direction,
  onClick,
  label,
}: {
  direction: 'prev' | 'next';
  onClick: () => void;
  label: string;
}) {
  const part = useBlockPart();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      {...part('pager', direction === 'prev' ? 0 : 1)}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [direction === 'prev' ? 'left' : 'right']: 'clamp(6px, 1.5vw, 14px)',
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        background: 'rgba(0,0,0,0.35)',
        color: 'rgba(255,255,255,0.95)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
      }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {direction === 'prev' ? (
          <polyline points="15 18 9 12 15 6" />
        ) : (
          <polyline points="9 18 15 12 9 6" />
        )}
      </svg>
    </button>
  );
}

// =============================================================================
// Styles
// =============================================================================

const gridStyle: CSSProperties = {
  ...cardReset,
  display: 'grid',
  // grid-template-columns is intentionally left to the scoped <style>
  // stylesheet (base rule + 992px media query) — an inline value here
  // would out-specificity the media query and the layout could never
  // switch to the sidebar+banner split, regardless of viewport width.
  gap: 'var(--space-4, 16px)',
  background: 'var(--color-surface-card, #fff)',
  borderRadius: 'var(--radius-lg, 16px)',
  padding: 'var(--space-3, 12px)',
};

// height: 100% + minHeight: 0 so the rail can be made to fill (and
// scroll within) whatever height the grid's align-items:stretch gives
// this column — i.e. the banner's actual rendered height — instead of
// an independent fixed cap that could run taller or shorter than the
// image next to it.
const catsColStyle: CSSProperties = {
  minWidth: 0,
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const bannerColStyle: CSSProperties = {
  minWidth: 0,
};

// Pre-load floor only — this 16/9 + maxHeight clamp is what shows before
// the active slide's image reports its real dimensions. Once it does,
// the inline style in BannerCarousel replaces both with the image's own
// ratio (see the `activeRatio` comment there). Cap is 450 (not some other
// number) specifically to match the rail's own 450 fallback (see
// `var(--hcb-banner-h, 450px)` and `maxHeightPx ?? 450` in CategoryRail)
// — otherwise the rail visibly resizes a second time once the image
// loads and this floor's real value stops being 400-ish vs 450.
const bannerPanelStyle: CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  aspectRatio: '16 / 9',
  maxHeight: 'clamp(160px, 30vw, 320px)',
  background:
    'linear-gradient(135deg, var(--color-brand-100, #26c6da) 0%, var(--color-brand-50, #006d82) 100%)',
};

// height: 100% cascades from catsColStyle; minHeight: 0 lets the flex
// child (the <ul>, flex: 1 below) actually shrink and scroll instead of
// growing the column past the banner's height.
const railWrapStyle: CSSProperties = {
  height: '100%',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

const railToggleStyle: CSSProperties = {
  flexShrink: 0,
  alignItems: 'center',
  gap: 'var(--space-2, 8px)',
  width: '100%',
  padding: 'var(--space-2, 8px)',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  fontWeight: 'var(--font-weight-semibold, 600)',
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-primary, #18181b)',
};

// display/flex-direction/collapse are owned entirely by railCss (scoped
// per-instance <style> below) — an inline value here would out-specificity
// the mobile-collapsed / desktop-forced-open class rules, same trap as
// the outer grid's column split.
const railListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  gap: 'var(--space-1, 6px)',
};

const railRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2, 8px)',
  padding: 'var(--space-2, 8px)',
  borderRadius: 'var(--radius-md, 8px)',
  textDecoration: 'none',
  color: 'var(--color-text-secondary, #52525b)',
  whiteSpace: 'nowrap',
};

const railIconStyle: CSSProperties = {
  width: 24,
  height: 24,
  borderRadius: 'var(--radius-sm, 6px)',
  flexShrink: 0,
};

const railLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function gridCss(uid: string): string {
  return `
    .hcb-${uid} { grid-template-columns: 1fr; }
    .hcb-row:hover { background: var(--color-surface-muted, #f4f4f5); }
    @media (min-width: 992px) {
      .hcb-${uid} { grid-template-columns: 260px 1fr; align-items: stretch; }
    }
  `;
}

// Mobile: collapsible accordion (toggle button + collapsed-by-default
// list, see the `open` state in CategoryRail) — same pattern CategoryTree
// uses for its collapsible panel. Desktop: the toggle is hidden and the
// list is forced visible (`!important` beats the `.hcb-rail-closed`
// combinator regardless of the `open` state a resize might have left
// behind) as a plain vertical column, capped to roughly 10 rows with a
// scrollbar for the rest — never one screen-height-tall runaway list.
function railCss(uid: string): string {
  return `
    .hcb-rail-toggle-${uid} { display: flex; }
    /* Mobile (accordion, expanded): vertical list, fixed-height scroll —
       not tied to the banner's height at all, since on mobile the two
       stack instead of sitting side by side. */
    .hcb-rail-list-${uid} {
      display: flex;
      flex-direction: column;
      max-height: 260px;
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
    }
    .hcb-rail-list-${uid}.hcb-rail-closed { display: none; }
    @media (min-width: 992px) {
      .hcb-rail-toggle-${uid} { display: none; }
      .hcb-rail-list-${uid} {
        display: flex !important;
        flex-direction: column;
        overflow-x: visible;
        /* Live JS measurement of the banner's real height, passed in as
           a CSS var (see the <ul> in CategoryRail) rather than a literal
           inline max-height — a literal would apply at every breakpoint
           and override the 260px mobile rule above. */
        max-height: var(--hcb-banner-h, 450px);
        overflow-y: auto;
        overscroll-behavior: contain;
      }
    }
  `;
}
